import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useTranslation } from 'react-i18next';
import Input from '@welldev-group/rcl-input';
import { debounce } from 'lodash';

import { itemsPerPage, maxPageNumberForFullLoad } from '../../variables';
import Header from '../common/Header';
import Paginate from '../common/Paginate';
import EmptyContentPlaceholder from '../common/EmptyContentPlaceholder';
import IconButton from '../rclComponents/IconButton';
import MetaIcon from '../icon/Icon';
import { GET_COUNTRIES, GET_CURSORS_FOR_COUNTRIES } from '../../graphql/queries/country';
import { DELETE_COUNTRY } from '../../graphql/mutations/country';
import GET_COUNT_OF_TOTAL_CURSORS from '../../graphql/queries/totalCursorsCount';
import SpinnerIcon from '../../assets/icons/spinner.svg';
import CountryTableRow from './CountryTableRow';
import isEntityAvailable from '../../helpers/isEntityAvailable';

const CountryList = () => {
  const { t } = useTranslation();
  const CURSOR_NUMBER_MULTIPLIER = 4;
  const COUNTRIES = 'countries';
  const SEARCH_DELAY = 1000;

  const [cursorState, setCursorState] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(3);
  const [currentCursor, setCurrentCursor] = useState(null);
  const [searchParam, setSearchParam] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [cursors, setCursors] = useState([]);
  const [cursorFetchNumber, setCursorFetchNumber] = useState(
    itemsPerPage * CURSOR_NUMBER_MULTIPLIER,
  );
  const [requiredLengthToSearch, setRequiredLengthToSearch] = useState(2);

  const [deleteItem] = useMutation(DELETE_COUNTRY);
  const cursorData = useQuery(GET_CURSORS_FOR_COUNTRIES, {
    variables: {
      first: cursorFetchNumber,
      after: currentCursor,
      query: searchParam,
    },
    fetchPolicy: 'network-only',
  });

  const cursorCountData = useQuery(GET_COUNT_OF_TOTAL_CURSORS, {
    variables: {
      modelName: COUNTRIES,
      query: searchParam,
    },
  });

  const { client, loading, data } = useQuery(GET_COUNTRIES, {
    variables: {
      first: itemsPerPage,
      after: currentCursor,
      query: searchParam,
    },
    fetchPolicy: 'network-only',
  });

  const callSearchParam = useRef(
    debounce(async inputParam => {
      setSearchParam(inputParam);
    }, SEARCH_DELAY),
  );

  const resetSearch = () => {
    setSearchParam('');
    setSearchValue('');
  };

  const postIcon = () => {
    if (loading) {
      return <img className="list__search-post-icon" src={SpinnerIcon} alt="spinner" />;
    }
    return (
      <IconButton
        size="large"
        className="list__search-post-icon"
        isIconOnly={true}
        icon={<MetaIcon name="close" />}
        onClick={resetSearch}
      />
    );
  };

  const forcePage = currentPage - 1;

  const listAttributes = ['name', 'phoneCode', 'nationality', 'code', 'descriptionStatus', 'apisDataRequired'];

  const changePage = useCallback(
    value => {
      setCurrentPage(value.selected + 1);
      const cursorIndexFromCurrentPage = value.selected * itemsPerPage - 1;
      if (cursorIndexFromCurrentPage < 0) {
        setCurrentCursor(null);
      } else {
        setCurrentCursor(cursors[cursorIndexFromCurrentPage]);
      }
    },
    [cursors],
  );

  useEffect(() => {
    if (cursorCountData && cursorCountData.data && !cursorCountData.loading) {
      const count = cursorCountData.data.totalCursorsCount / itemsPerPage;
      const pageCount = Math.ceil(count);
      setRequiredLengthToSearch(2);
      if (pageCount <= maxPageNumberForFullLoad) {
        setCursorFetchNumber(null);
      } else {
        setCursorFetchNumber(itemsPerPage * CURSOR_NUMBER_MULTIPLIER);
      }
      if (pageCount < currentPage) {
        changePage({ selected: 0 });
      }
      setPageCount(pageCount);
    }
    if (cursorData && cursorData.data && !cursorData.loading && !cursorState) {
      setCursorState(true);
      const oldCursors = Object.assign([], cursors);
      const newCursors = cursorData.data[COUNTRIES].edges.map(edge => edge.cursor);
      const finalCursors = oldCursors.concat(
        newCursors.filter(newCursor => oldCursors.indexOf(newCursor) < 0),
      );
      setCursors(finalCursors);
    }
  }, [cursorCountData, cursorData, changePage, currentPage, cursors, cursorState]);

  const resetCountries = () => client.resetStore();

  const onDelete = id => {
    window.confirm(t('countries.index.confirmDelete')) &&
      deleteItem({
        variables: { id },
      }).then(() => resetCountries());
  };

  const onSearchParamChange = value => {
    setSearchValue(value);
    if (value === '' || value.length >= requiredLengthToSearch) {
      callSearchParam.current(value);
    }
  };

  return (
    <div className="country-list">
      <Header title="nav.countries" linkTo="/admin/countries/new" buttonLabel="shared.addNew" />
      <div>
        <Input
          label={t('list.search.label')}
          value={searchValue}
          onChange={onSearchParamChange}
          placeholder={t('countries.index.searchPlaceholder')}
          postIcon={postIcon()}
        />
      </div>
      <div>
        {loading && <div className="lds-dual-ring" />}
        {!loading && (
          <React.Fragment>
            {isEntityAvailable(data, COUNTRIES) ? (
              <>
                <div className="country-list__pagination">
                  <Paginate
                    pageCount={pageCount}
                    onPageChange={changePage}
                    forcePage={forcePage}
                    marginPagesDisplayed={cursorFetchNumber ? 0 : 1}
                  />
                </div>
                <div className="country-list__header">
                  {listAttributes.map(item => (
                    <div className="country-list__data-value" key={item}>
                      <p>{t(`countries.attributes.${item}`)}</p>
                    </div>
                  ))}
                  <div className="country-list__data-value country-list__data-value--header-actions">
                    <p>{t('list.header.actions')}</p>
                  </div>
                </div>
                <div className="country-list__table">
                  {data[COUNTRIES].edges.map(data => (
                    <CountryTableRow
                      data={data.node}
                      onDelete={onDelete}
                      key={data.node.id}
                      listAttributes={listAttributes}
                      resetCountries={resetCountries}
                    />
                  ))}
                </div>
                <div className="country-list__pagination">
                  <Paginate
                    pageCount={pageCount}
                    onPageChange={changePage}
                    forcePage={forcePage}
                    marginPagesDisplayed={cursorFetchNumber ? 0 : 1}
                  />
                </div>
              </>
            ) : (
              <EmptyContentPlaceholder
                entityName={t('nav.countries').toLowerCase()}
                modelName={'Country'}
              />
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

export default CountryList;
